import type { EventFilter, PlatformEvent } from '../types/events';

/**
 * Runtime-gated diagnostics for the platform-event / run-stream pipeline.
 *
 * Why this exists: we see runs where the chat UI shows the "Thinking…" CoT
 * placeholder, then jumps straight to the final answer with no intermediate
 * reasoning/tool steps — i.e. the runId-scoped subscriber never received the
 * `agent.run.*` step events and completion was caught only by the poll
 * fallback. This module records the four signals needed to tell apart the
 * candidate causes (server tier-gating, WS-not-open-at-invoke, conversationId
 * early-window miss, subjectId mismatch) and feeds the on-screen HUD.
 *
 * Gating: OFF unless `localStorage['optr:debug:events'] === '1'` or the page
 * URL carries `?evtdbg=1`. Deliberately NOT gated on NODE_ENV so it can be
 * switched on against staging *and* production. When disabled, every record*
 * call is a cheap early-return and nothing is logged or stored.
 *
 * Toggle at runtime from the console: `__eventDebug.on()` / `.off()` / `.dump()`.
 */

const FLAG_KEY = 'optr:debug:events';
/** Where exportFixture() stashes the captured run so /dev/cot-playback can
 *  replay it without a source edit. Read by loadImportedFixture(). */
export const COT_FIXTURE_KEY = 'optr:debug:cot-fixture';
const LOG_PREFIX = '[evt-dbg]';
const MAX_RUNS = 12;
/** Cap retained full payloads per run so a long-lived run can't grow unbounded. */
const MAX_EVENTS_PER_RUN = 2000;

/** Replayable capture shape — mirrors `PlaybackFixture` in
 *  `src/pages/dev/cot-playback-fixtures.ts` (kept structural to avoid a
 *  dev-page import from this prod util). */
export interface CapturedFixture {
  id: string;
  title: string;
  description: string;
  events: PlatformEvent[];
  parentRunId?: string;
  childRunIds?: string[];
}

export interface RunDebugInfo {
  runId: string;
  startedAt: number;
  /** WS connection state captured at the moment invoke() handed off. */
  connectedAtStart: boolean;
  /** Per-eventType counts of events that reached the runId subscriber. */
  counts: Record<string, number>;
  firstEventAt?: number;
  lastEventAt?: number;
  /** Set when the inactivity timer expired and HTTP polling took over. */
  pollStarted?: boolean;
  /** How the terminal arrived — discriminates WS vs poll completion. */
  terminalVia?: 'ws' | 'poll';
  terminalKind?: string;
}

export interface EventDebugState {
  enabled: boolean;
  wsConnected: boolean;
  wsReadyState: number | null;
  /** The most recent filter union pushed to the server via `{action:'set'}`. */
  lastFilters: EventFilter[];
  lastFiltersAt: number;
  /** Total inbound WS frames dispatched as platform events. */
  inboundTotal: number;
  /** Inbound frames that matched zero local subscriptions (possible miss). */
  unmatchedTotal: number;
  activeRunId?: string;
  /** Most-recent-first, capped at MAX_RUNS. */
  runs: RunDebugInfo[];
}

function computeEnabled(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(FLAG_KEY) === '1') {
      return true;
    }
  } catch {
    /* localStorage may be unavailable (SSR / sandboxed) */
  }
  try {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('evtdbg') === '1'
    ) {
      return true;
    }
  } catch {
    /* no window */
  }
  return false;
}

class EventDebugStore {
  private enabled = computeEnabled();
  private wsConnected = false;
  private wsReadyState: number | null = null;
  private lastFilters: EventFilter[] = [];
  private lastFiltersAt = 0;
  private inboundTotal = 0;
  private unmatchedTotal = 0;
  private activeRunId: string | undefined;
  private runs = new Map<string, RunDebugInfo>();
  /** Full event payloads per run, retained only while enabled. Keyed by runId,
   *  capped per run and to the last MAX_RUNS runs (mirrors `runs`). */
  private runEvents = new Map<string, PlatformEvent[]>();
  private listeners = new Set<() => void>();
  private version = 0;
  private snapshot: EventDebugState = this.buildSnapshot();

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    try {
      if (on) localStorage.setItem(FLAG_KEY, '1');
      else localStorage.removeItem(FLAG_KEY);
    } catch {
      /* ignore */
    }
    this.bump();
  }

  /** Tagged console line, only when enabled. */
  log(tag: string, message: string, data?: unknown): void {
    if (!this.enabled) return;
    if (data !== undefined) console.debug(`${LOG_PREFIX}[${tag}]`, message, data);
    else console.debug(`${LOG_PREFIX}[${tag}]`, message);
  }

  recordConnection(connected: boolean, readyState: number | null): void {
    if (!this.enabled) return;
    this.wsConnected = connected;
    this.wsReadyState = readyState;
    this.log('ws', connected ? 'connected' : 'disconnected', { readyState });
    this.bump();
  }

  recordFlush(filters: EventFilter[], readyState: number | null): void {
    if (!this.enabled) return;
    this.lastFilters = filters;
    this.lastFiltersAt = Date.now();
    this.wsReadyState = readyState;
    this.log('flush', `set ${filters.length} filter(s)`, { readyState, filters });
    this.bump();
  }

  recordInbound(event: PlatformEvent, matchedCount: number): void {
    if (!this.enabled) return;
    this.inboundTotal += 1;
    if (matchedCount === 0) {
      this.unmatchedTotal += 1;
      const data = event.data as Record<string, unknown> | undefined;
      this.log('inbound-unmatched', event.eventType, {
        subjectId: event.subjectId,
        runId: data?.runId,
        parentRunId: data?.parentRunId,
      });
    }
    this.bump();
  }

  recordRunStart(runId: string, connectedAtStart: boolean): void {
    if (!this.enabled) return;
    this.runs.set(runId, {
      runId,
      startedAt: Date.now(),
      connectedAtStart,
      counts: {},
    });
    this.trimRuns();
    this.log('run-start', runId, { connectedAtStart });
    this.bump();
  }

  recordRunEvent(runId: string, eventType: string): void {
    if (!this.enabled) return;
    const run = this.runs.get(runId);
    if (!run) return;
    run.counts[eventType] = (run.counts[eventType] ?? 0) + 1;
    const now = Date.now();
    run.firstEventAt ??= now;
    run.lastEventAt = now;
    this.bump();
  }

  /**
   * Retain a full event payload for `runId` so it can be exported as a
   * replayable fixture. No-op when disabled (the common case), so the call
   * site can be unconditional. Ring-buffered per run + capped to MAX_RUNS.
   */
  recordRunEventFull(runId: string, event: PlatformEvent): void {
    if (!this.enabled || !runId) return;
    let list = this.runEvents.get(runId);
    if (!list) {
      list = [];
      this.runEvents.set(runId, list);
      this.trimRunEvents();
    }
    list.push(event);
    if (list.length > MAX_EVENTS_PER_RUN) list.splice(0, list.length - MAX_EVENTS_PER_RUN);
  }

  /**
   * Builds a replayable {@link CapturedFixture} from the captured events for
   * `runId` (defaults to the active run), persists it to localStorage under
   * {@link COT_FIXTURE_KEY} so /dev/cot-playback can pick it up, and returns it
   * for console copy/download. Returns null when nothing was captured.
   */
  exportFixture(runId?: string): CapturedFixture | null {
    const id = runId ?? this.activeRunId;
    if (!id) {
      console.warn(`${LOG_PREFIX} exportFixture: no runId and no active run`);
      return null;
    }
    const events = this.runEvents.get(id);
    if (!events || events.length === 0) {
      console.warn(`${LOG_PREFIX} exportFixture: no captured events for ${id} (enable with __eventDebug.on() before the run)`);
      return null;
    }

    // Derive parent/child runIds from the captured stream. The exported run is
    // the parent; any event carrying a different effective runId is a child.
    const childRunIds = new Set<string>();
    for (const e of events) {
      const data = e.data as Record<string, unknown> | undefined;
      const eff = e.subjectId ?? (data?.runId as string | undefined);
      if (eff && eff !== id) childRunIds.add(eff);
      const parent = data?.parentRunId as string | undefined;
      if (parent && parent !== id) childRunIds.add(eff ?? '');
    }
    childRunIds.delete('');

    const fixture: CapturedFixture = {
      id: `captured-${id}`,
      title: `Captured — ${id} (${events.length} events)`,
      description: `Captured live via __eventDebug.exportFixture() for diagnosing the stuck-CoT bug.`,
      events,
      parentRunId: id,
      ...(childRunIds.size > 0 ? { childRunIds: [...childRunIds] } : {}),
    };

    try {
      localStorage.setItem(COT_FIXTURE_KEY, JSON.stringify(fixture));
      console.log(`${LOG_PREFIX} exportFixture: saved ${events.length} events to localStorage['${COT_FIXTURE_KEY}'] — open /dev/cot-playback and pick "Imported (from capture)"`);
    } catch (err) {
      console.warn(`${LOG_PREFIX} exportFixture: localStorage write failed (too large?)`, err);
    }
    return fixture;
  }

  recordPollStart(runId: string): void {
    if (!this.enabled) return;
    const run = this.runs.get(runId);
    if (run) run.pollStarted = true;
    this.log('poll-start', runId, { note: 'WS inactivity → HTTP poll fallback' });
    this.bump();
  }

  recordTerminal(runId: string, kind: string, via: 'ws' | 'poll'): void {
    if (!this.enabled) return;
    const run = this.runs.get(runId);
    if (run) {
      run.terminalKind = kind;
      run.terminalVia = via;
    }
    this.log('terminal', `${runId} ${kind} via ${via}`);
    this.bump();
  }

  setActiveRun(runId: string | undefined): void {
    if (!this.enabled) return;
    this.activeRunId = runId;
    this.bump();
  }

  dump(): EventDebugState {
    const s = this.getSnapshotState();
    console.log(`${LOG_PREFIX} state`, s);
    return s;
  }

  private trimRuns(): void {
    if (this.runs.size <= MAX_RUNS) return;
    const keys = [...this.runs.keys()];
    for (const k of keys.slice(0, this.runs.size - MAX_RUNS)) this.runs.delete(k);
  }

  private trimRunEvents(): void {
    if (this.runEvents.size <= MAX_RUNS) return;
    const keys = [...this.runEvents.keys()];
    for (const k of keys.slice(0, this.runEvents.size - MAX_RUNS)) this.runEvents.delete(k);
  }

  private buildSnapshot(): EventDebugState {
    return {
      enabled: this.enabled,
      wsConnected: this.wsConnected,
      wsReadyState: this.wsReadyState,
      lastFilters: this.lastFilters,
      lastFiltersAt: this.lastFiltersAt,
      inboundTotal: this.inboundTotal,
      unmatchedTotal: this.unmatchedTotal,
      activeRunId: this.activeRunId,
      runs: [...this.runs.values()].sort((a, b) => b.startedAt - a.startedAt),
    };
  }

  private bump(): void {
    this.version += 1;
    this.snapshot = this.buildSnapshot();
    for (const l of this.listeners) l();
  }

  // useSyncExternalStore wiring — getVersion is the stable snapshot value.
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getVersion = (): number => this.version;

  getSnapshotState = (): EventDebugState => this.snapshot;
}

export const eventDebug = new EventDebugStore();

// Console handle for toggling without a UI.
declare global {
  interface Window {
    __eventDebug?: {
      on: () => void;
      off: () => void;
      dump: () => EventDebugState;
      exportFixture: (runId?: string) => CapturedFixture | null;
    };
  }
}

if (typeof window !== 'undefined') {
  window.__eventDebug = {
    on: () => {
      eventDebug.setEnabled(true);
      console.log(`${LOG_PREFIX} enabled — reload not required`);
    },
    off: () => eventDebug.setEnabled(false),
    dump: () => eventDebug.dump(),
    exportFixture: (runId?: string) => eventDebug.exportFixture(runId),
  };
}
